import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { MapPin, Route, Bell, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';

export const LogitrakSection = () => {
  const { t } = useLanguage();

  const features = [
    { icon: MapPin, text: t('logitrak_feature_1') },
    { icon: Route, text: t('logitrak_feature_2') },
    { icon: Bell, text: t('logitrak_feature_3') },
  ];

  const fadeUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.5, ease: 'easeOut' }
  };

  return (
    <section 
      id="logitrak" 
      className="py-16 md:py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden"
      data-testid="logitrak-section"
    >
      {/* Background grid */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px]"></div>
      
      {/* Glow effect */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-green-500/10 rounded-full blur-[150px]"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <motion.div {...fadeUp}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-500/20 rounded-full text-green-400 text-sm font-medium mb-6">
              <MapPin className="w-4 h-4" />
              {t('logitrak_badge')}
            </div>
            
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4 tracking-tight" data-testid="logitrak-title">
              {t('logitrak_title')}
            </h2>
            <h3 className="text-xl md:text-2xl text-green-400 font-semibold mb-6">
              {t('logitrak_subtitle')}
            </h3>
            <p className="text-slate-300 text-base md:text-lg leading-relaxed mb-8">
              {t('logitrak_desc')}
            </p>

            {/* Feature list */}
            <div className="space-y-4 mb-8">
              {features.map((feature, index) => (
                <motion.div 
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.4 }}
                  className="flex items-center gap-3"
                >
                  <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
                    <feature.icon className="w-4 h-4 text-green-400" />
                  </div>
                  <span className="text-slate-200 font-medium">{feature.text}</span>
                </motion.div>
              ))}
            </div>

            <Button 
              asChild
              size="lg"
              className="bg-green-500 hover:bg-green-600 text-white rounded-md px-8 py-6 text-base font-medium transition-all hover:shadow-lg hover:shadow-green-500/25"
              data-testid="logitrak-cta"
            >
              <a href="https://logitrak.ch" target="_blank" rel="noopener noreferrer">
                {t('logitrak_cta')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </Button>
          </motion.div>

          {/* Map/GPS Visualization */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="relative"
            data-testid="logitrak-visual"
          >
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 shadow-2xl">
              {/* Mock map header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                    <MapPin className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-white font-semibold">Logitrak GPS</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <span className="text-green-400 text-sm">Live</span>
                </div>
              </div>

              {/* Mock map area */}
              <div className="relative h-64 bg-slate-900/50 rounded-xl overflow-hidden">
                {/* Grid lines */}
                <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:20px_20px]"></div>
                
                {/* Mock vehicle markers */}
                <div className="absolute top-1/4 left-1/3">
                  <div className="w-4 h-4 bg-green-500 rounded-full animate-ping absolute"></div>
                  <div className="w-4 h-4 bg-green-500 rounded-full relative z-10 flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                </div>
                <div className="absolute top-1/2 right-1/4">
                  <div className="w-4 h-4 bg-blue-500 rounded-full animate-ping absolute" style={{ animationDelay: '0.5s' }}></div>
                  <div className="w-4 h-4 bg-blue-500 rounded-full relative z-10 flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                </div>
                <div className="absolute bottom-1/3 left-1/2">
                  <div className="w-4 h-4 bg-green-500 rounded-full animate-ping absolute" style={{ animationDelay: '1s' }}></div>
                  <div className="w-4 h-4 bg-green-500 rounded-full relative z-10 flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                </div>

                {/* Route line */}
                <svg className="absolute inset-0 w-full h-full" style={{ zIndex: 1 }}>
                  <path 
                    d="M 120 64 Q 200 150 280 128" 
                    stroke="rgba(34, 197, 94, 0.5)" 
                    strokeWidth="2" 
                    fill="none" 
                    strokeDasharray="5,5"
                  />
                </svg>
              </div>

              {/* Vehicle status */}
              <div className="mt-4 grid grid-cols-3 gap-3">
                <div className="bg-slate-900/50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-white">8</div>
                  <div className="text-xs text-slate-400">En route</div>
                </div>
                <div className="bg-slate-900/50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-green-400">12</div>
                  <div className="text-xs text-slate-400">Disponibles</div>
                </div>
                <div className="bg-slate-900/50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-white">4</div>
                  <div className="text-xs text-slate-400">En maintenance</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
